import React, { useRef, useState } from 'react';
import { FiUpload, FiX, FiImage, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { adminApi } from '../adminApi';
import { showAlert } from '../utils/dialog';

export default function MultiImageUpload({ values = [], onChange, max = 3 }) {
  const [uploading, setUploading] = useState(false);
  const multiRef = useRef(null);
  const singleRefs = [useRef(null), useRef(null), useRef(null)];

  // Upload multiple files at once
  const handleMultiFile = async (e) => {
    const files = Array.from(e.target.files).slice(0, max - values.length);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = await Promise.all(files.map(f => adminApi.uploadImage(f).then(r => r.data.url)));
      const updated = [...values, ...uploaded].slice(0, max);
      onChange(updated.filter(Boolean));
    } catch {
      showAlert('Upload failed. Use JPG, PNG or WebP under 10 MB.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Replace single slot
  const handleSingleFile = async (e, idx) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const r = await adminApi.uploadImage(file);
      const updated = [...values];
      updated[idx] = r.data.url;
      onChange(updated.filter(Boolean));
    } catch {
      showAlert('Upload failed. Use JPG, PNG or WebP under 10 MB.');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    const updated = [...values];
    updated.splice(idx, 1);
    onChange(updated.filter(Boolean));
  };

  const moveLeft = (idx) => {
    if (idx === 0) return;
    const updated = [...values];
    [updated[idx - 1], updated[idx]] = [updated[idx], updated[idx - 1]];
    onChange(updated);
  };

  const moveRight = (idx) => {
    if (idx === values.length - 1) return;
    const updated = [...values];
    [updated[idx], updated[idx + 1]] = [updated[idx + 1], updated[idx]];
    onChange(updated);
  };

  const slots = Array.from({ length: max }, (_, i) => values[i] || null);
  const canAddMore = values.length < max;

  return (
    <div className="admin-form-group">
      <label>
        Product Images *{' '}
        <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '.78rem' }}>
          (First image is main · Max {max})
        </span>
      </label>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
        {slots.map((url, idx) => (
          <div key={idx} style={{ width: 110, flexShrink: 0 }}>
            {url ? (
              <div style={{ position: 'relative' }}>
                {/* Image preview */}
                <img
                  src={url}
                  alt={`Image ${idx + 1}`}
                  style={{ width: 110, height: 110, objectFit: 'contain', borderRadius: 8, border: idx === 0 ? '2px solid var(--accent)' : '1.5px solid var(--border)', background: '#f8fafc', display: 'block', cursor: 'pointer' }}
                  onClick={() => singleRefs[idx].current?.click()}
                  title="Click to replace"
                />

                {/* Replace input */}
                <input ref={singleRefs[idx]} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleSingleFile(e, idx)} />

                {/* Remove button */}
                <button type="button" onClick={() => removeImage(idx)}
                  style={{ position: 'absolute', top: 4, right: 4, width: 20, height: 20, borderRadius: '50%', background: 'rgba(239,68,68,0.9)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                  <FiX size={11} />
                </button>

                {/* Main badge */}
                {idx === 0 && (
                  <span style={{ position: 'absolute', bottom: 28, left: 0, right: 0, textAlign: 'center', background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: '.58rem', fontWeight: 700, padding: '2px 0' }}>
                    MAIN
                  </span>
                )}

                {/* Position arrows */}
                <div style={{ display: 'flex', justifyContent: 'center', gap: 4, marginTop: 4 }}>
                  <button type="button" onClick={() => moveLeft(idx)} disabled={idx === 0}
                    style={{ flex: 1, padding: '3px 0', border: '1px solid var(--border)', borderRadius: 4, background: idx === 0 ? '#f1f5f9' : '#fff', cursor: idx === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: idx === 0 ? '#cbd5e1' : '#334155' }}>
                    <FiChevronLeft size={13} />
                  </button>
                  <button type="button" onClick={() => moveRight(idx)} disabled={idx === values.length - 1}
                    style={{ flex: 1, padding: '3px 0', border: '1px solid var(--border)', borderRadius: 4, background: idx === values.length - 1 ? '#f1f5f9' : '#fff', cursor: idx === values.length - 1 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: idx === values.length - 1 ? '#cbd5e1' : '#334155' }}>
                    <FiChevronRight size={13} />
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ width: 110, height: 110, border: '1.5px dashed var(--border)', borderRadius: 8, background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiImage size={22} color="#cbd5e1" />
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Upload button */}
      <input
        ref={multiRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={handleMultiFile}
      />
      {canAddMore && (
        <button type="button" onClick={() => multiRef.current?.click()} disabled={uploading}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
            width: '100%', padding: '10px 14px',
            border: '1.5px dashed var(--border)', borderRadius: 8,
            background: 'var(--bg)', color: 'var(--text-muted)',
            cursor: uploading ? 'wait' : 'pointer', fontSize: '.875rem',
            fontFamily: 'inherit', transition: 'all .15s',
          }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.color = 'var(--accent)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
        >
          {uploading
            ? <><FiUpload size={14} /> Uploading…</>
            : <><FiImage size={14} /> {values.length === 0 ? 'Upload images (select up to 3 at once)' : `Upload more (${max - values.length} remaining)`}</>
          }
        </button>
      )}
      <div style={{ fontSize: '.72rem', color: 'var(--text-muted)', marginTop: 6 }}>
        Recommended: 800 × 800 px · JPG or WebP · Click image to replace · Use arrows to reorder
      </div>
    </div>
  );
}
